import type { Auftrag, AuftragsTyp } from '@awf/shared';
import { useEffect, useMemo, useState } from 'react';
import { AUFTRAGSTYPEN, TYP_REIHENFOLGE, typName } from '../auftragstypen.js';
import listenStile from './run-list-page.module.css';
import { alsMonat, kontingentSkala, NUTZUNGSKONTINGENT, nutzungNachMonat } from './nutzung.js';
import stile from './nutzungs-statistik.module.css';

/**
 * Balken mit Strich beim Kontingent, in Kachel und Tabelle gleich. Nur Bild: Die Zahl daneben
 * traegt die Aussage, deshalb `aria-hidden`.
 */
export function KontingentBalken({
  durchlaeufe,
  mitStrich = true,
}: {
  durchlaeufe: number;
  /** In der Tabelle ohne Strich (Rueckmeldung 2026-10-16): Dort steht "x von 200" je Zeile. */
  mitStrich?: boolean;
}) {
  const { fuellung, strich } = kontingentSkala(durchlaeufe);
  return (
    <span className={stile.balken} aria-hidden="true" data-testid="kontingent-balken">
      <span className={stile.fuellung} style={{ inlineSize: `${fuellung}%` }} />
      {mitStrich && (
        <span
          className={stile.strich}
          style={{ insetInlineStart: `${strich}%` }}
          data-testid="kontingent-strich"
        />
      )}
    </span>
  );
}

/** Ein halbes Jahr: genug fuer einen Verlauf, ohne die Liste darunter weit nach unten zu schieben. */
export const STATISTIK_MONATE = 6;

export interface NutzungsStatistikProps {
  auftraege: readonly Auftrag[];
  /** Dieselbe Auswahl wie "Angelegt von" der Liste, gleiche Namen. */
  personen: readonly { uid: string; text: string }[];
  jetzt: Date;
  /** Am Hol-Deckel: der aelteste geladene Monat ist angeschnitten. */
  gedeckelt: boolean;
}

/**
 * Monatliche Nutzung der Gesamtansicht. Eigene Filter statt der Listenfilter: Die Statistik
 * beantwortet "wie viel im Monat", die Liste "welcher Auftrag" -- ein Typwechsel unten soll die
 * Monatsreihe oben nicht still umdeuten. Das Kontingent gilt nur uebergreifend; unter einem
 * Filter waere "x von 200" ein Vergleich mit einer Groesse, die es je Workflow oder Person nicht
 * gibt.
 */
export function NutzungsStatistik({
  auftraege,
  personen,
  jetzt,
  gedeckelt,
}: NutzungsStatistikProps) {
  const [typ, setTyp] = useState<AuftragsTyp | 'alle'>('alle');
  const [gewaehltePerson, setPerson] = useState('');
  // Verschwindet die Person aus dem Bestand, faellt die Auswahl auf alle zurueck, statt eine
  // leere Reihe zu einer Option zu zeigen, die es nicht mehr gibt.
  const vorhanden = personen.some((p) => p.uid === gewaehltePerson);
  const person = vorhanden ? gewaehltePerson : null;
  // ... und zwar endgueltig (Codex-Gate PR #91): Bliebe die uid im Zustand, griffe der alte Filter
  // ohne Zutun wieder, sobald die Person zurueckkehrt.
  useEffect(() => {
    if (gewaehltePerson !== '' && !vorhanden) setPerson('');
  }, [gewaehltePerson, vorhanden]);
  const uebergreifend = typ === 'alle' && person === null;

  const monate = useMemo(
    () => nutzungNachMonat(auftraege, { typ, person }, jetzt, STATISTIK_MONATE),
    [auftraege, typ, person, jetzt],
  );

  return (
    <section className={stile.bereich} aria-labelledby="nutzungsstatistik">
      <div className={listenStile.kopfzeile}>
        <h2 id="nutzungsstatistik" className={listenStile.titel}>
          Monatliche Nutzung
        </h2>
      </div>

      <div className={listenStile.filterleiste}>
        <select
          className={listenStile.auswahl}
          aria-label="Workflow der Statistik"
          value={typ}
          onChange={(e) => setTyp(e.target.value as AuftragsTyp | 'alle')}
        >
          <option value="alle">Alle Workflows</option>
          {TYP_REIHENFOLGE.map((t) => (
            <option key={t} value={t}>
              {typName(t)}
            </option>
          ))}
        </select>
        <select
          className={listenStile.auswahl}
          aria-label="Person der Statistik"
          value={person ?? ''}
          onChange={(e) => setPerson(e.target.value)}
        >
          <option value="">Alle Personen</option>
          {personen.map((p) => (
            <option key={p.uid} value={p.uid}>
              {p.text}
            </option>
          ))}
        </select>
      </div>

      <div className={listenStile.tabellenrahmen}>
        <table className={`${listenStile.tabelle} ${stile.tabelle}`}>
          <thead>
            <tr>
              <th scope="col">Monat</th>
              {typ === 'alle' &&
                TYP_REIHENFOLGE.map((t) => (
                  <th key={t} scope="col" className={stile.zahl}>
                    {AUFTRAGSTYPEN[t].text}
                  </th>
                ))}
              <th scope="col" className={stile.zahl}>
                Durchläufe
              </th>
              {person === null && (
                <th scope="col" className={stile.zahl}>
                  Personen
                </th>
              )}
              {uebergreifend && (
                <th scope="col" className={stile.kontingent}>
                  Kontingent
                </th>
              )}
            </tr>
          </thead>
          <tbody>
            {monate.map((m) => (
              <tr key={m.monat}>
                <th scope="row" className={stile.monat}>
                  {alsMonat(m.monat)}
                </th>
                {typ === 'alle' &&
                  TYP_REIHENFOLGE.map((t) => (
                    <td key={t} className={stile.zahl}>
                      {m.jeTyp[t]}
                    </td>
                  ))}
                <td className={`${stile.zahl} ${stile.summe}`}>{m.durchlaeufe}</td>
                {person === null && <td className={stile.zahl}>{m.personen}</td>}
                {uebergreifend && (
                  <td className={stile.kontingent}>
                    <span className={stile.kontingentText}>
                      {m.durchlaeufe} von {NUTZUNGSKONTINGENT}
                    </span>
                    <KontingentBalken durchlaeufe={m.durchlaeufe} mitStrich={false} />
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {gedeckelt && (
        <p className={listenStile.hinweis} data-testid="statistik-deckel">
          Ausgewertet werden die zuletzt angelegten Aufträge; im ältesten gezeigten Monat können
          Durchläufe fehlen.
        </p>
      )}
    </section>
  );
}
